import { Link } from 'react-router-dom';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { useUnits, kmToDisplay, unitLabel, paceSecPerUnit, formatEventDistance } from '../../lib/units';
import { OfficialBadge, CommunityBadge, VerifiedBadge } from '../../components/common/Badges';
import { Card } from '../../components/ui/card';
import { FloatingActionMenu } from '../../components/common/FloatingActionMenu';
import { MapPin, Calendar, Activity, Trophy, Loader2, Footprints, Gauge, Target, Clock, Radio } from 'lucide-react';
import { formatEventDate, formatPace, countdownTo } from '../../lib/formatters';

export default function MobileHome() {
  const { units } = useUnits();
  // Discover: upcoming events that the user has NOT already joined.
  const { data: events, loading: eventsLoading } = useApi(
    () => api.getEvents({ excludeJoined: true }),
    [],
  );
  const { data: active } = useApi(api.getActiveParticipation);
  const { data: friendsFeed, loading: friendsFeedLoading } = useApi(api.getFriendsFeed);
  const { data: stats } = useApi(api.getMyStats);
  // Registered: only upcoming events the user has joined (past goes to Events → Past tab).
  const { data: myEvents, loading: myEventsLoading } = useApi(
    () => api.getMyEvents({ past: false }),
    [],
  );

  // Find the closest upcoming registered event for the "highlight" card
  const now = Date.now();
  const upcomingRegistered = (myEvents || [])
    .filter((e) => new Date(e.date).getTime() > now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const nextEvent = upcomingRegistered[0];

  return (
    <div className="px-4 pt-4 pb-24 space-y-6" data-testid="mobile-home">
      {/* My workout — first thing on the page. Each card links to Profile
          with a hash anchor so we can auto-scroll to the relevant section. */}
      <section data-testid="home-stats">
        <h2 className="rivl-label mb-3">My workout</h2>
        <div className="grid grid-cols-2 gap-3">
          <StatBlock to="/profile#recent-runs" icon={Footprints} label="Total runs" value={stats?.totalRuns ?? 0} testid="stat-total-runs" />
          <StatBlock to="/profile#recent-runs" icon={Target} label={`Total ${unitLabel(units)}`} value={kmToDisplay(stats?.totalKm ?? 0, units).toFixed(1)} testid="stat-total-distance" />
          <StatBlock to="/profile#recent-runs" icon={Activity} label="Total steps" value={formatSteps(stats?.totalKm ?? 0)} testid="stat-total-steps" />
          <StatBlock to="/profile#stats" icon={Gauge} label="Best pace" value={stats?.bestPaceSec ? `${formatPace(paceSecPerUnit(stats.bestPaceSec, units))}/${unitLabel(units)}` : '—'} testid="stat-best-pace" />
        </div>
      </section>

      {/* Registered events */}
      <section data-testid="home-registered-events">
        <div className="flex items-center justify-between mb-3">
          <h2 className="rivl-label">Registered events</h2>
          {myEvents?.length > 0 && (
            <Link to="/events" className="text-xs text-primary font-medium">See all →</Link>
          )}
        </div>

        {myEventsLoading ? (
          <div className="space-y-3" data-testid="registered-events-skeleton">
            <SkeletonRow tall />
            <SkeletonRow />
          </div>
        ) : (!myEvents || myEvents.length === 0) ? (
          <div className="rivl-card p-6 text-center">
            <Calendar className="h-6 w-6 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-2">No registered events yet.</p>
            <Link to="/events" className="text-xs text-primary font-medium mt-2 inline-block">Browse events →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {nextEvent && <UpcomingEventCard event={nextEvent} />}
            {myEvents.filter((e) => e.id !== nextEvent?.id).slice(0, 3).map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                className="rivl-card px-4 py-3 flex items-center gap-3 hover:bg-secondary/30 transition-colors"
                data-testid={`registered-event-${e.id}`}
              >
                <div
                  className="h-12 w-12 rounded-md bg-cover bg-center border border-border flex-shrink-0"
                  style={{ backgroundImage: `url(${e.image})` }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground">{formatEventDate(e.date)} · {formatEventDistance(e.distanceKm, units)}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                  {prettyState(e.myState)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Discover events */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="rivl-label">Discover events</h2>
          <Link to="/events" className="text-xs text-primary font-medium" data-testid="see-all-events">See all →</Link>
        </div>
        {eventsLoading && (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
        {!eventsLoading && events?.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">No events open right now. Check back soon.</p>
        )}
        {!eventsLoading && events?.length > 0 && (
          <div className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-2 snap-x snap-mandatory">
            {events.map((e) => (
              <Link
                to={`/events/${e.id}`}
                key={e.id}
                data-testid={`discover-card-${e.id}`}
                className="snap-start min-w-[260px] rivl-card overflow-hidden hover:border-primary transition-colors"
              >
                <div className="h-32 bg-cover bg-center relative" style={{ backgroundImage: `url(${e.image})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-2 left-2">
                    {e.type === 'official' ? <OfficialBadge /> : <CommunityBadge />}
                  </div>
                  <div className="absolute bottom-2 left-2 text-white">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-90">
                      {formatEventDate(e.date)} · {formatEventDistance(e.distanceKm, units)}
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-display font-semibold text-sm leading-tight">{e.name}</div>
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {e.location}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Activity feed = friends' recent runs */}
      <section>
        <h2 className="rivl-label mb-3">Friends' activity</h2>
        {friendsFeedLoading ? (
          <div className="space-y-2" data-testid="friends-feed-skeleton">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : friendsFeed?.length > 0 ? (
          <Card className="rivl-card divide-y divide-border" data-testid="friends-activity-feed">
            {friendsFeed.map((f) => (
              <div key={f.id} className="flex items-start gap-3 p-4">
                <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center text-xs font-semibold">
                  {f.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex-1">
                  <p className="text-sm leading-snug">
                    <span className="font-semibold">{f.name}</span>{' '}
                    <span className="text-muted-foreground">{f.text}</span>
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>{relTime(f.createdAt)}</span>
                    {f.verified && <VerifiedBadge />}
                  </div>
                </div>
                {f.type === 'finish' && <Trophy className="h-4 w-4 text-primary mt-1" />}
                {f.type === 'pr' && <Activity className="h-4 w-4 text-primary mt-1" />}
              </div>
            ))}
          </Card>
        ) : (
          <div className="rivl-card p-6 text-center">
            <Activity className="h-6 w-6 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-2">No friend activity yet.</p>
            <Link to="/friends" className="text-xs text-primary font-medium mt-2 inline-block">Add friends →</Link>
          </div>
        )}
      </section>

      <FloatingActionMenu />
    </div>
  );
}

function UpcomingEventCard({ event }) {
  const { units } = useUnits();
  const cd = countdownTo(event.date);
  const isLive = event.raceState === 'live';
  return (
    <Link
      to={isLive ? `/events/${event.id}/leaderboard` : `/events/${event.id}`}
      className={`block rounded-md overflow-hidden border-2 ${isLive ? 'border-success bg-success/10' : 'border-primary bg-primary/5'}`}
      data-testid="upcoming-event-card"
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className="h-16 w-16 rounded-md bg-cover bg-center border border-border flex-shrink-0"
          style={{ backgroundImage: `url(${event.image})` }}
        />
        <div className="flex-1 min-w-0">
          {isLive ? (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-success" data-testid="upcoming-event-live">
              <Radio className="h-3 w-3 animate-pulse" /> LIVE NOW
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-primary">
              <Clock className="h-3 w-3" /> Upcoming
              {cd?.isSoon && <span className="ml-1 px-1.5 py-0.5 rounded bg-primary text-primary-foreground">SOON</span>}
            </span>
          )}
          <div className="text-sm font-bold mt-0.5 truncate">{event.name}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{formatEventDate(event.date)} · {formatEventDistance(event.distanceKm, units)}</div>
          {!isLive && event.joinDeadline && new Date(event.joinDeadline).getTime() > Date.now() && (
            <div className="text-[10px] text-primary/80 mt-1 flex items-center gap-1" data-testid="upcoming-deadline">
              <Clock className="h-2.5 w-2.5" /> Reg. closes {formatEventDate(event.joinDeadline)}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          {isLive ? (
            <div className="font-display font-black text-sm tabular-nums text-success">
              Watch →
            </div>
          ) : (
            <>
              <div className="rivl-label text-[8px]">starts in</div>
              <div className="font-mono font-black text-base tabular-nums text-primary" data-testid="upcoming-event-countdown">
                {cd?.label || '—'}
              </div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

function StatBlock({ icon: Icon, label, value, to, testid }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <span className="rivl-label text-[9px]">{label}</span>
        <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={2} />
      </div>
      <div className="font-mono font-bold text-2xl mt-1 tabular-nums">{value}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="rivl-card p-3.5 hover:border-primary transition-colors" data-testid={testid}>
        {inner}
      </Link>
    );
  }
  return <div className="rivl-card p-3.5" data-testid={testid}>{inner}</div>;
}

function SkeletonRow({ tall = false }) {
  return (
    <div className={`rivl-card p-3 flex items-center gap-3 animate-pulse ${tall ? 'h-[88px]' : 'h-[68px]'}`}>
      <div className={`${tall ? 'h-16 w-16' : 'h-12 w-12'} rounded-md bg-secondary/60 flex-shrink-0`} />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-secondary/60 rounded w-3/5" />
        <div className="h-2.5 bg-secondary/40 rounded w-2/5" />
      </div>
    </div>
  );
}

function prettyState(s) {
  if (!s) return '';
  if (s === 'verified') return 'Verified';
  if (s === 'finished') return 'Finished';
  if (s === 'unverified') return 'Rejected';
  if (s === 'under-review') return 'Review';
  if (s === 'live') return 'Live';
  if (s === 'ready') return 'Ready';
  if (s === 'checked-in') return 'Checked-in';
  return s.replace(/-/g, ' ');
}

function relTime(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}


// Steps are unit-agnostic — they're always derived from raw km (the
// underlying number is the same whether the user views in km or mi).
const STEPS_PER_KM = 1300;
function formatSteps(km) {
  const n = Math.round((Number(km) || 0) * STEPS_PER_KM);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}
